import { Divider } from '@mui/material';
import { OptionallyVisible } from 'components';
import { FieldArrayRenderProps, getIn, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { FormField } from 'components/form/formField/formField';
import IconAdd from 'components/icons/add';
import IconRemove from 'components/icons/dash';
import FileDropzone from 'components/inputs/file/dropzone';
import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { SelectItem } from 'components/inputs/select/select';

import { FormType, LABELS, imgAcceptRules } from '../../keys';
import { Section } from '../../styles';
import { BridgeIdentityType, IdentifyingInformationType } from '../../types';
import { createFileUploadHandler } from '../../useFileUpload';
import { createFormikChangeHandler, getCombinedError, getSectionErrors } from '../../utils/formikHelpers';
import { isFieldDisabledInPartialMode } from '../../utils/partialUpdateHelpers';
import replaceLabelsUnderscore from '../../utils/replaceLabelsUnderscore';
import { FieldStatusBadge, PersonFieldStatusBadge } from '../fieldStatusBadge/FieldStatusBadge';
import FormikInput from '../formikTextInput/formikTextInput';
import { Block, Container, InnerLabel, Label, LabelRow, RecordButton } from './styles';

interface Props {
  // path to the field in the formik values, must point to a field of an array type: IdentifyingInformationType[]
  fieldPath: string;
  insert: FieldArrayRenderProps['insert'];
  remove: FieldArrayRenderProps['remove'];

  countries: SelectItem[];
  //  for errors coming from backend
  error?: Record<string, string>;
  clearBackendErrors?: () => void;
  isPartialMode?: boolean;
  personUuid?: string;
}

const IDENTITY_TYPES: SelectItem[] = Object.entries(BridgeIdentityType).map(([key, value]) => ({
  key: value,
  label: key,
}));

const formattedIdentityTypes = replaceLabelsUnderscore(IDENTITY_TYPES);

const IdentifyingInformation = ({
  fieldPath,
  error,
  countries,
  insert,
  remove,
  clearBackendErrors,
  isPartialMode = false,
  personUuid,
}: Props) => {
  const { t } = useTranslation();
  const { values, touched, errors, setFieldValue } = useFormikContext<FormType>();
  const identifyingInformationValues = getIn(values, fieldPath) as IdentifyingInformationType[];

  const disabled = isFieldDisabledInPartialMode(fieldPath, isPartialMode);

  const addItem = () => {
    insert(identifyingInformationValues.length + 1, { type: undefined, issuingCountry: undefined, number: '' });
  };

  const removeItem = (index: number) => {
    return () => {
      remove(index);
    };
  };

  return (
    <>
      <LabelRow>
        <Label>{t(LABELS.SECTIONS.IDENTIFYING_INFORMATION.TITLE)}</Label>
        {personUuid ? (
          <PersonFieldStatusBadge fieldName="identifyingInformation" personUuid={personUuid} />
        ) : (
          <FieldStatusBadge fieldName={fieldPath} />
        )}
      </LabelRow>

      {getSectionErrors(error, fieldPath).map((message) => (
        <FormField key={message} error={message}>
          {null}
        </FormField>
      ))}

      <Block>
        <Section>
          {identifyingInformationValues.map((identifyingInformation, index) => {
            const type = `${fieldPath}[${index}].type`;
            const country = `${fieldPath}[${index}].issuingCountry`;
            const number = `${fieldPath}[${index}].number`;
            const imageFront = `${fieldPath}[${index}].imageFront`;

            const typeError = getIn(errors, type);
            const typeTouched = getIn(touched, type);

            const countryError = getIn(errors, country);
            const countryTouched = getIn(touched, country);

            const imageFrontError = getIn(errors, imageFront);
            const imageFrontTouched = getIn(touched, imageFront);
            const imageFrontVal = getIn(values, imageFront);

            const handleFileChange = createFileUploadHandler(setFieldValue, imageFront, imageFrontVal);

            return (
              <Container>
                <FormField error={typeError && typeTouched ? typeError : error?.[type]}>
                  <SearchableSelect<SelectItem, false>
                    fullWidth
                    value={formattedIdentityTypes.find((item) => item.key === identifyingInformation.type)}
                    items={formattedIdentityTypes}
                    placeholder={t(LABELS.SECTIONS.IDENTIFYING_INFORMATION.PLACEHOLDER_TYPE)}
                    onChange={(selectItem) => {
                      setFieldValue(type, selectItem?.key);
                    }}
                    getItemLabel={(item) => item.label}
                    multiple={false}
                    closeDropdownOnSelect
                    error={(typeError && typeTouched) || Boolean(error?.[type])}
                    disabled={disabled}
                  />
                </FormField>

                <FormField error={countryError && countryTouched ? countryError : error?.[country]}>
                  <SearchableSelect<SelectItem, false>
                    fullWidth
                    value={countries.find((item) => item.key === identifyingInformation.issuingCountry)}
                    items={countries}
                    placeholder={t(LABELS.SECTIONS.IDENTIFYING_INFORMATION.PLACEHOLDER_COUNTRY)}
                    onChange={(selectItem) => {
                      setFieldValue(country, selectItem?.key);
                    }}
                    getItemLabel={(item) => item.label}
                    multiple={false}
                    closeDropdownOnSelect
                    error={(countryError && countryTouched) || Boolean(error?.[country])}
                    disabled={disabled}
                  />
                </FormField>

                <FormikInput
                  placeholder={t(LABELS.SECTIONS.IDENTIFYING_INFORMATION.PLACEHOLDER_NUMBER)}
                  fieldPath={number}
                  error={error}
                  disabled={disabled}
                />

                <FormField error={getCombinedError(error, imageFront, imageFrontError, imageFrontTouched)}>
                  <InnerLabel>{t(LABELS.SECTIONS.DOCUMENTS.LOGO.UPLOAD)}</InnerLabel>
                  <FileDropzone
                    error={imageFrontError && imageFrontTouched ? imageFrontError : undefined}
                    onFileChange={createFormikChangeHandler(handleFileChange, clearBackendErrors)}
                    filledInputLabel={t(LABELS.SECTIONS.DOCUMENTS.LOGO.LABEL)}
                    initialFile={imageFrontVal}
                    hints={[t(LABELS.SECTIONS.DOCUMENTS.LOGO.SUPPORTED_FORMAT), t(LABELS.SECTIONS.DOCUMENTS.LOGO.MAX_FILE_SIZE)]}
                    accept={imgAcceptRules}
                    disabled={disabled}
                    maxFileSizeInMB={20}
                  />
                </FormField>

                <OptionallyVisible visible={index > 0}>
                  <div>
                    <RecordButton type="button" inside flat onClick={removeItem(index)} disabled={disabled}>
                      <IconRemove />
                      {t(LABELS.SECTIONS.IDENTIFYING_INFORMATION.REMOVE)}
                    </RecordButton>
                  </div>
                </OptionallyVisible>

                <OptionallyVisible visible={index !== identifyingInformationValues.length - 1}>
                  <Divider />
                </OptionallyVisible>
              </Container>
            );
          })}
        </Section>
      </Block>

      <RecordButton type="button" flat onClick={addItem} disabled={disabled}>
        <IconAdd />
        {t(LABELS.SECTIONS.IDENTIFYING_INFORMATION.ADD)}
      </RecordButton>
    </>
  );
};

export default IdentifyingInformation;
